import { apiClient } from './client';

export interface SurveySession {
  id: string;
  survey_id: string;
  user_id: string;
  attempt_number: number;
  status: 'in_progress' | 'completed' | 'abandoned';
  progress_percentage: number;
  responses: Record<string, any>;
  time_spent_seconds: number;
  current_question_index: number;
  started_at: string;
  last_activity_at: string;
  completed_at?: string;
  organization_id?: string;
}

export interface SessionStats {
  total_attempts: number;
  completed_attempts: number;
  abandoned_attempts: number;
  average_time_spent: number;
  average_progress: number;
  best_attempt?: SurveySession;
  latest_attempt?: SurveySession;
}

export interface SessionUpdate {
  responses?: Record<string, any>;
  progress_percentage?: number;
  time_spent_seconds?: number;
  current_question_index?: number;
}

export interface CanAttemptResponse {
  can_attempt: boolean;
  attempts_used: number;
  min_days_between_attempts?: number;
  has_active_session: boolean;
  active_session_id?: string;
  reason?: string;
}

class SessionsAPI {
  async startSession(surveyId: string): Promise<SurveySession> {
    const response = await apiClient.post(`/surveys/${surveyId}/sessions/start`);
    return response.data;
  }

  async getSession(sessionId: string): Promise<SurveySession> {
    const response = await apiClient.get(`/sessions/${sessionId}`);
    return response.data;
  }

  async saveProgress(sessionId: string, data: SessionUpdate): Promise<SurveySession> {
    const response = await apiClient.put(`/sessions/${sessionId}/save`, data);
    return response.data;
  }

  async completeSession(sessionId: string, finalResponses?: Record<string, any>): Promise<SurveySession> {
    const response = await apiClient.post(`/sessions/${sessionId}/complete`, { 
      final_responses: finalResponses 
    });
    return response.data;
  }

  async abandonSession(sessionId: string): Promise<SurveySession> {
    const response = await apiClient.post(`/sessions/${sessionId}/abandon`);
    return response.data;
  }

  async getSurveySessions(surveyId: string): Promise<SurveySession[]> {
    const response = await apiClient.get(`/surveys/${surveyId}/sessions`);
    return response.data;
  }

  async getMySessions(statusFilter?: string): Promise<SurveySession[]> {
    const params = statusFilter ? { status_filter: statusFilter } : {};
    const response = await apiClient.get('/users/me/sessions', { params });
    return response.data;
  }

  async getSessionStats(surveyId: string): Promise<SessionStats> {
    const response = await apiClient.get(`/surveys/${surveyId}/sessions/stats`);
    return response.data;
  }

  async checkCanAttempt(surveyId: string): Promise<CanAttemptResponse> {
    const response = await apiClient.get(`/surveys/${surveyId}/can-attempt`);
    return response.data;
  }
}

export const sessionsAPI = new SessionsAPI();

// Auto-save helper with debouncing
let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;

export function setupAutoSave(
  sessionId: string, 
  getData: () => SessionUpdate,
  interval: number = 5000
): () => void {
  const doSave = async () => {
    try {
      const data = getData();
      await sessionsAPI.saveProgress(sessionId, data);
      console.log('Progress auto-saved');
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  // Clear any existing timeout
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
  }

  // Set up recurring auto-save
  autoSaveTimeout = setInterval(doSave, interval) as any;

  // Return cleanup function
  return () => {
    if (autoSaveTimeout) {
      clearInterval(autoSaveTimeout);
      autoSaveTimeout = null;
    }
  };
}

// Export types
// Note: SurveySession, SessionStats, SessionUpdate, CanAttemptResponse are already exported above